import { useState, useEffect, useCallback } from 'react';
// ts
import { IViewState } from 'src/@infringements/interfaces';
// providers
// stores
import { dateRangeStore } from 'src/@app/stores/dateRangeStore';
// functions
import {
  getDateArray,
  getDateMapping,
  addSourceStyleToMap,
  removeSourceStylesFromMap,
  isMapLoaded,
  getAdditionalMapBoxConfig,
  getMapDate,
} from 'src/utils/mapHelpers';
// constants
import { CHANGE_EVENT } from 'src/constants';
import { useConfigSelectors } from 'src/@app/reducers/configReducer';

/**
 * Custom hook to get ref
 */

export const useMapRef = () => {
  const [map, setMap] = useState(null);
  const mapRef = useCallback(map => {
    setMap(map);
  }, []);
  return [map, mapRef];
};

/**
 * Custom hook to get map ref
 * @param map - Mapbox API
 */

export const useMapApis = map => {
  const [mapApis, setMapApis] = useState(null);
  useEffect(() => {
    if (map) {
      // when map is ready
      setMapApis(map.getMap());
    }
  }, [map]);

  return mapApis;
};

/**
 * Custom hook that returns props used for the StyledMap component
 */

export const useMapProps = (mode?: string) => {
  const configSelectors = useConfigSelectors();
  const {
    map: {
      centre: { latitude, longitude, altitude },
      zoom,
      mapAccessToken,
      mapStyleURL,
      mapStyleURLDarkMode,
      dragRotate,
      minZoom,
      maxZoom,
      pitch,
      bearing,
    },
  } = configSelectors.getConfig();

  const viewportFromProps: IViewState = {
    latitude: parseFloat(latitude),
    longitude: parseFloat(longitude),
    zoom,
    pitch,
    bearing,
  };

  if (altitude) {
    viewportFromProps.altitude = altitude;
  }
  if (mode === '2D') {
    viewportFromProps.maxPitch = 0;
  }

  return {
    viewportFromProps,
    mapboxApiAccessToken: mapAccessToken,
    mapStyle: mapStyleURL,
    mapStyleDark: mapStyleURLDarkMode,
    dragRotate,
    minZoom,
    maxZoom,
  };
};

/**
 * Add the map sources with the selected date-range
 * @param mapApis - Mapbox API
 * @param datesArray - array of date strings
 * @param mapBoxConfig - map config from db
 */

export const addSourcesStylesToMap = (
  mapApis: any,
  datesArray: string[],
  mapBoxConfig: any,
  layers: any[]
) => {
  if (mapApis) {
    isMapLoaded(mapApis).then(() => {
      addSourceStyleToMap(mapApis, datesArray, mapBoxConfig, layers);
    });
  }
};

/**
 * Removes sources from map
 * @param mapApis - Mapbox API
 * @param datesArray - array of date strings
 * @param mapBoxConfig - map config from db
 */

export const removeSourcesStylesFromMap = (
  mapApis: any,
  datesArray: string[],
  mapBoxConfig,
  layers
) => {
  isMapLoaded(mapApis).then(() => {
    datesArray.forEach((dateString: string) => {
      removeSourceStylesFromMap(mapApis, dateString, mapBoxConfig, layers);
    });
  });
};

/**
 * Provides map config required for mapbox
 */

export const useMapConfig = () => {
  const configSelectors = useConfigSelectors();
  const {
    globals: { protectedServices },
    map: { mapboxUser, sitePrefix, sourcePrefix, sourceLayerPrefix, geocoderBbox, geocoderLimit },
  } = configSelectors.getConfig();

  const servicesLength = protectedServices.length;
  // values from config
  const mapBoxConfig = {
    mapboxUser,
    sitePrefix,
    sourcePrefix,
    sourceLayerPrefix,
    geocoderBbox,
    geocoderLimit,
    elevationEndpoint: 'https://api.mapbox.com/v4/mapbox.mapbox-terrain-v2/tilequery',
    reverseGeocodingEndpoint: 'https://api.mapbox.com/geocoding/v5/mapbox.places',
    operationPrefix: 'Operations_',
    protectedServices,
    transformRequest: accessToken => {
      return url => {
        for (let i = servicesLength; i--; ) {
          if (url.includes(protectedServices[i])) {
            return {
              url,
              headers: {
                Authorization: 'Bearer ' + accessToken,
              },
            };
            break;
          }
        }
      };
    },
  };
  // providing mapbox config with additional constants
  return Object.assign({}, mapBoxConfig, getAdditionalMapBoxConfig(mapBoxConfig));
};

/**
 * Hook to update map on changes to dateRangeStore
 * @param map - Mapbox API
 */
export const useDatesDataForMap = (mapApis, mapBoxConfig, layers) => {
  const [datesArray, updateDatesArray] = useState([]);
  const [dateRangeMapping, updateDateRangeMapping] = useState({});

  useEffect(() => {
    const handleDateRangeUpdates = () => {
      const previousDateRange = dateRangeStore.getPreviousDateRange();
      const previousDates = previousDateRange ? getDateArray(previousDateRange) : previousDateRange;

      // get filter data from store and extract dates
      const dateFilters = dateRangeStore.getDateFilters();
      const datesData = getDateArray(dateFilters);
      updateDatesArray(datesData);

      // generate date mapping from dates
      const dateMapping = getDateMapping(dateFilters);
      updateDateRangeMapping(dateMapping);
      if (layers && layers.length) {
        if (previousDates && previousDates.length) {
          removeSourcesStylesFromMap(mapApis, previousDates, mapBoxConfig, layers);
        }
        // add sources
        addSourcesStylesToMap(mapApis, datesData, mapBoxConfig, layers);
      }
    };

    // when map is ready
    if (mapApis) {
      handleDateRangeUpdates();

      // attach store listeners
      dateRangeStore.on(CHANGE_EVENT, handleDateRangeUpdates);
      return () => {
        dateRangeStore.removeListener(CHANGE_EVENT, handleDateRangeUpdates);
      };
    }
  }, [mapApis, layers]);

  return {
    datesArray,
    dateRangeMapping,
  };
};

export const useMapCenter = (viewport, setViewport, position, operationId) => {
  useEffect(() => {
    if (position.latitude >= -90 && position.latitude <= 90) {
      viewport.latitude = position.latitude;
    }
    if (position.longitude >= -180 && position.longitude <= 180) {
      viewport.longitude = position.longitude;
    }
    viewport.zoom = 10;
    setViewport(viewport);
  }, [operationId]);
};

export const useSingleDateTile = (mapApis, mapBoxConfig, layers, time, operationId) => {
  const [dateString, updateDateString] = useState('');
  const [datesArray, updateDatesArray]: any[] = useState([]);

  useEffect(() => {
    const mapDate = getMapDate(new Date(time.slice(0, 10)));
    updateDateString(mapDate);
    updateDatesArray([mapDate]);
    if (mapApis) {
      addSourcesStylesToMap(mapApis, [mapDate], mapBoxConfig, layers);
      return () => {
        // TODO remove tile
      };
    }
  }, [mapApis, operationId]);
  return { dateString, datesArray };
};
